import { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { dashboardService } from '../services/dashboard';

const TIER_LABELS = {
  equal: 'Equal',
  better: 'Better',
  cost_effective: 'Cost effective',
};

const PROVIDER_LABELS = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google',
};

const formatPrice = (value) => `$${Number(value).toFixed(2)}`;

const formatQualityDelta = (delta) => {
  if (delta > 0) return `+${delta} quality`;
  if (delta < 0) return `${delta} quality`;
  return 'same quality';
};

const countEqualReplaces = (replaces) => replaces.filter((r) => r.tier === 'equal').length;

const ReplacesCount = ({ replaces }) => {
  const equal = countEqualReplaces(replaces);
  const total = replaces.length;
  return (
    <>
      {equal}
      <span className="text-xs opacity-50">{` / ${total}`}</span>
    </>
  );
};

export default function ModelsPage() {
  const [osModels, setOsModels] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    dashboardService.models(
      (res) => {
        setOsModels(res.data.os_models || []);
        setProviders(res.data.providers || []);
      },
      console.error,
    );
  }, []);

  return (
    <DashboardLayout title="Models">
      <p className="text-sm opacity-70 mb-6">
        The open-source models we route to, the closed models each one replaces, and the providers
        that serve them.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left opacity-60 border-b border-theme-secondary">
              <th className="py-2">Model</th>
              <th className="py-2">Quality</th>
              <th className="py-2">Modalities</th>
              <th className="py-2">From</th>
              <th className="py-2">Speed</th>
              <th className="py-2">Replaces</th>
              <th className="py-2">Providers</th>
            </tr>
          </thead>
          <tbody>
            {osModels.map((model) => (
              <tr
                key={model.id}
                onClick={() => setSelected(model)}
                className="border-b border-theme-secondary/50 cursor-pointer hover:bg-theme-secondary/30"
              >
                <td className="py-3 pr-4">
                  <div className="font-mono text-theme-primary">{model.id}</div>
                  <div className="text-xs opacity-60 mt-0.5">{model.blurb}</div>
                </td>
                <td className="py-3 font-mono">{model.quality}</td>
                <td className="py-3">{model.modalities.join(', ')}</td>
                <td className="py-3 font-mono">{formatPrice(model.cheapest_blended)}/M</td>
                <td className="py-3 font-mono">{model.fastest_tps} tps</td>
                <td className="py-3"><ReplacesCount replaces={model.replaces} /></td>
                <td className="py-3">
                  <div className="flex flex-wrap gap-1.5">
                    {model.offerings.map((o) => (
                      <span
                        key={o.provider}
                        className={`text-[11px] px-2 py-0.5 rounded-full border ${
                          o.configured ? 'border-theme-primary text-theme-primary' : 'border-theme-secondary opacity-50'
                        }`}
                      >
                        {o.provider_name}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
            {osModels.length === 0 && (
              <tr><td colSpan={7} className="py-8 text-center opacity-50">No models.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {providers.length > 0 && (
        <p className="text-xs opacity-50 mt-6">
          Providers:{' '}
          {providers.map((p, i) => (
            <span key={p.id}>
              {i > 0 && ', '}
              {p.name}
              {p.configured ? ' (connected)' : ' (no key)'}
            </span>
          ))}
        </p>
      )}

      {selected && <ModelDetail model={selected} onClose={() => setSelected(null)} />}
    </DashboardLayout>
  );
}

function ModelDetail({ model, onClose }) {
  const [showAllTiers, setShowAllTiers] = useState(false);
  const equalReplaces = model.replaces.filter((r) => r.tier === 'equal');
  const otherReplaces = model.replaces.filter((r) => r.tier !== 'equal');
  const visibleReplaces = showAllTiers ? model.replaces : equalReplaces;
  const unique = Object.values(
    Object.fromEntries(model.replaces.map((r) => [r.input_model, r])),
  );
  const avg = (key) => (unique.length ? unique.reduce((sum, r) => sum + r[key], 0) / unique.length : 0);
  const avgCost = avg('orig_blended');
  const avgQuality = avg('orig_quality');
  const avgTps = avg('orig_tps');
  const speedMultiple = avgTps ? model.fastest_tps / avgTps : 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-theme-main border border-theme-secondary p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-1">
          <h2 className="text-lg font-medium font-mono text-theme-primary break-all">{model.id}</h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 opacity-50 hover:opacity-100 text-lg leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <p className="text-sm opacity-70 mb-4">{model.blurb}</p>

        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm mb-6">
          <span>Quality index <span className="font-mono">{model.quality}</span></span>
          <span>From <span className="font-mono">{formatPrice(model.cheapest_blended)}/M</span> blended</span>
          <span>Up to <span className="font-mono">{model.fastest_tps} tps</span></span>
          <span>Modalities {model.modalities.join(', ')}</span>
        </div>

        {unique.length > 0 && (
          <>
            <h3 className="text-sm font-medium mb-1">Impact</h3>
            <p className="text-xs opacity-60 mb-3">
              This model vs the average of the {unique.length} closed models it replaces.
            </p>
            <div className="space-y-4 mb-6">
              <CompareBar
                label="Cost (blended $/M)"
                caption={avgCost ? `~${Math.round((1 - model.cheapest_blended / avgCost) * 100)}% cheaper` : null}
                thisValue={model.cheapest_blended}
                otherValue={avgCost}
                max={Math.max(model.cheapest_blended, avgCost)}
                format={(v) => formatPrice(v)}
              />
              <CompareBar
                label="Speed (tokens/sec)"
                caption={speedMultiple >= 1.2 ? `~${speedMultiple.toFixed(1)}x faster` : null}
                thisValue={model.fastest_tps}
                otherValue={avgTps}
                max={Math.max(model.fastest_tps, avgTps)}
                format={(v) => `${Math.round(v)} tps`}
              />
              <CompareBar
                label="Quality (index ~0-60)"
                caption={formatQualityDelta(Math.round(model.quality - avgQuality))}
                thisValue={model.quality}
                otherValue={avgQuality}
                max={60}
                format={(v) => Math.round(v)}
              />
            </div>
          </>
        )}

        <h3 className="text-sm font-medium mb-2">Provider options</h3>
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left opacity-60 border-b border-theme-secondary">
                <th className="py-2">Provider</th>
                <th className="py-2">Input $/M</th>
                <th className="py-2">Output $/M</th>
                <th className="py-2">Blended</th>
                <th className="py-2">Speed</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {model.offerings.map((o) => (
                <tr key={o.provider} className="border-b border-theme-secondary/50">
                  <td className="py-2">{o.provider_name}</td>
                  <td className="py-2 font-mono">{formatPrice(o.price_in)}</td>
                  <td className="py-2 font-mono">{formatPrice(o.price_out)}</td>
                  <td className="py-2 font-mono">{formatPrice(o.blended)}</td>
                  <td className="py-2 font-mono">{o.tps} tps</td>
                  <td className="py-2">
                    <span className={`text-xs ${o.configured ? 'text-theme-primary' : 'opacity-50'}`}>
                      {o.configured ? 'Connected' : 'No key'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 className="text-sm font-medium mb-2">Replaces</h3>
        {model.replaces.length === 0 ? (
          <p className="text-sm opacity-60">No closed models route to this model yet.</p>
        ) : (
          <>
            <div className="space-y-2">
              {visibleReplaces.map((r) => (
                <div
                  key={`${r.input_model}-${r.tier}`}
                  className="rounded-lg border border-theme-secondary px-4 py-3"
                >
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <span className="font-mono text-sm">{r.input_model}</span>
                    <span className="text-[10px] uppercase tracking-wide opacity-50">
                      {PROVIDER_LABELS[r.provider] || r.provider} · {TIER_LABELS[r.tier]}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs opacity-80 mb-1">
                    {r.savings_pct != null && <span>{r.savings_pct}% cheaper</span>}
                    <span>{formatQualityDelta(r.quality_delta)}</span>
                    <span className="opacity-60">
                      {PROVIDER_LABELS[r.provider] || r.provider} {formatPrice(r.orig_blended)}/M blended
                    </span>
                  </div>
                  <p className="text-xs opacity-60">{r.helper_text}</p>
                </div>
              ))}
            </div>
            {otherReplaces.length > 0 && (
              <button
                type="button"
                onClick={() => setShowAllTiers((v) => !v)}
                className="mt-3 text-sm text-theme-primary opacity-70 hover:opacity-100"
              >
                {showAllTiers
                  ? 'Show equal tier only'
                  : `+${otherReplaces.length} other model${otherReplaces.length === 1 ? '' : 's'} at other tiers`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CompareBar({ label, caption, thisValue, otherValue, max, format }) {
  const width = (v) => `${Math.max((v / (max || 1)) * 100, 2)}%`;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-xs opacity-60">{label}</span>
        {caption && <span className="text-xs text-theme-primary font-medium">{caption}</span>}
      </div>
      <Bar name="This model" value={thisValue} width={width(thisValue)} format={format} highlight />
      <Bar name="Replaced avg" value={otherValue} width={width(otherValue)} format={format} />
    </div>
  );
}

function Bar({ name, value, width, format, highlight }) {
  return (
    <div className="flex items-center gap-3 mb-1">
      <span className="w-24 shrink-0 text-xs opacity-70">{name}</span>
      <div className="flex-1 h-4 rounded bg-theme-secondary/25 overflow-hidden">
        <div
          className={`h-full rounded ${highlight ? 'bg-theme-primary' : 'bg-[color-mix(in_srgb,var(--theme-text)_65%,var(--theme-secondary-bg))]'}`}
          style={{ width }}
        />
      </div>
      <span className="w-16 shrink-0 text-right text-xs font-mono">{format(value)}</span>
    </div>
  );
}
